/**
 * listingFieldEnhancements.js
 *
 * Enrichissements frontend pour les champs de type `text` ou `number`
 * du catalogue backend qui nécessitent des valeurs contrôlées.
 *
 * - overrideType: 'select'  → transforme un champ text en liste déroulante
 * - options                 → tableau d'objets { value, en, ar }
 *                             (value = clé stockée = label français)
 * - min / max               → contraintes pour les champs number
 */

const currentYear = new Date().getFullYear();

// ─── OPTIONS PAR THÈME ────────────────────────────────────────────────────────

const colorOptions = [
  { value: 'Blanc',    en: 'White',     ar: 'أبيض',   colorHex: '#FFFFFF' },
  { value: 'Noir',     en: 'Black',     ar: 'أسود',   colorHex: '#1A1A1A' },
  { value: 'Gris',     en: 'Gray',      ar: 'رمادي',  colorHex: '#9E9E9E' },
  { value: 'Argent',   en: 'Silver',    ar: 'فضي',    colorHex: '#C0C0C0' },
  { value: 'Or',       en: 'Gold',      ar: 'ذهبي',   colorHex: '#FFD700' },
  { value: 'Rouge',    en: 'Red',       ar: 'أحمر',   colorHex: '#E53935' },
  { value: 'Orange',   en: 'Orange',    ar: 'برتقالي', colorHex: '#FB8C00' },
  { value: 'Jaune',    en: 'Yellow',    ar: 'أصفر',   colorHex: '#FDD835' },
  { value: 'Vert',     en: 'Green',     ar: 'أخضر',   colorHex: '#43A047' },
  { value: 'Bleu',     en: 'Blue',      ar: 'أزرق',   colorHex: '#1E88E5' },
  { value: 'Violet',   en: 'Purple',    ar: 'بنفسجي', colorHex: '#8E24AA' },
  { value: 'Rose',     en: 'Pink',      ar: 'وردي',   colorHex: '#E91E8C' },
  { value: 'Marron',   en: 'Brown',     ar: 'بني',    colorHex: '#6D4C41' },
  { value: 'Beige',    en: 'Beige',     ar: 'بيج',    colorHex: '#F5F0E8' },
  { value: 'Bordeaux', en: 'Burgundy',  ar: 'عنابي',  colorHex: '#7B1E3A' },
  { value: 'Turquoise',en: 'Turquoise', ar: 'تركواز', colorHex: '#00BCD4' },
  { value: 'Autre',    en: 'Other',     ar: 'أخرى'   }
];

const materialOptions = [
  { value: 'Bois',         en: 'Wood',            ar: 'خشب'            },
  { value: 'Métal',        en: 'Metal',            ar: 'معدن'           },
  { value: 'Acier',        en: 'Steel',            ar: 'فولاذ'          },
  { value: 'Aluminium',    en: 'Aluminum',         ar: 'ألومينيوم'      },
  { value: 'Inox',         en: 'Stainless steel',  ar: 'ستانلس ستيل'    },
  { value: 'Plastique',    en: 'Plastic',          ar: 'بلاستيك'        },
  { value: 'Cuir',         en: 'Leather',          ar: 'جلد'            },
  { value: 'Tissu',        en: 'Fabric',           ar: 'قماش'           },
  { value: 'Coton',        en: 'Cotton',           ar: 'قطن'            },
  { value: 'Lin',          en: 'Linen',            ar: 'كتان'           },
  { value: 'Soie',         en: 'Silk',             ar: 'حرير'           },
  { value: 'Caoutchouc',   en: 'Rubber',           ar: 'مطاط'           },
  { value: 'Verre',        en: 'Glass',            ar: 'زجاج'           },
  { value: 'Céramique',    en: 'Ceramic',          ar: 'سيراميك'        },
  { value: 'Pierre',       en: 'Stone',            ar: 'حجر'            },
  { value: 'Béton',        en: 'Concrete',         ar: 'خرسانة'         },
  { value: 'Composite',    en: 'Composite',        ar: 'مواد مركبة'     },
  { value: 'Autre',        en: 'Other',            ar: 'أخرى'           }
];

const languageOptions = [
  { value: 'Français',           en: 'French',          ar: 'الفرنسية'     },
  { value: 'Arabe',              en: 'Arabic',          ar: 'العربية'      },
  { value: 'Anglais',            en: 'English',         ar: 'الإنجليزية'   },
  { value: 'Berbère/Tamazight',  en: 'Berber/Tamazight',ar: 'الأمازيغية'   },
  { value: 'Espagnol',           en: 'Spanish',         ar: 'الإسبانية'    },
  { value: 'Allemand',           en: 'German',          ar: 'الألمانية'    },
  { value: 'Italien',            en: 'Italian',         ar: 'الإيطالية'    },
  { value: 'Portugais',          en: 'Portuguese',      ar: 'البرتغالية'   },
  { value: 'Russe',              en: 'Russian',         ar: 'الروسية'      },
  { value: 'Turc',               en: 'Turkish',         ar: 'التركية'      },
  { value: 'Chinois',            en: 'Chinese',         ar: 'الصينية'      },
  { value: 'Autre',              en: 'Other',           ar: 'أخرى'         }
];

// ─── TABLE DES AMÉLIORATIONS ─────────────────────────────────────────────────

/**
 * Clé = key du champ dans le catalogue.
 *
 * Propriétés possibles :
 *   overrideType  {string}  – 'select' pour forcer un champ text → liste
 *   options       {Array}   – [{ value, en, ar }]  (overrideType requis)
 *   min           {number}  – borne inférieure pour un champ number
 *   max           {number}  – borne supérieure pour un champ number
 */
export const FIELD_ENHANCEMENTS = {
  // ── Couleur (vehicle + product) ──────────────────────────────────────────
  couleur: {
    overrideType: 'select',
    options: colorOptions
  },

  // ── Matière principale (product) ─────────────────────────────────────────
  matiere_principale: {
    overrideType: 'select',
    options: materialOptions
  },

  // ── Langues parlées (service) ─────────────────────────────────────────────
  langues_parlees: {
    overrideType: 'select',
    options: languageOptions
  },

  // ── Contraintes sur les champs number ────────────────────────────────────
  annee:                  { min: 1900, max: currentYear },
  annee_achat:            { min: 1960, max: currentYear },
  kilometrage:            { min: 0, max: 9_999_999 },
  nb_chambres:            { min: 0, max: 50 },
  nb_salles_bain:         { min: 0, max: 20 },
  nb_toilettes:           { min: 0, max: 20 },
  nb_lits:                { min: 0, max: 100 },
  nb_portes:              { min: 2, max: 100 },
  nb_places_assises:      { min: 1, max: 100 },
  nb_places:              { min: 1, max: 50 },
  superficie:             { min: 1, max: 100_000 },
  surface_terrain:        { min: 1, max: 1_000_000 },
  etage:                  { min: 0, max: 200 },
  capacite_accueil:       { min: 1, max: 500 },
  puissance_cv:           { min: 1, max: 5_000 },
  puissance_w:            { min: 0, max: 100_000 },
  coffre_volume:          { min: 0, max: 5_000 },
  consommation_mixte:     { min: 0, max: 100 },
  autonomie:              { min: 0, max: 10_000 },
  poids:                  { min: 0, max: 100_000 },
  niveau_bruit_db:        { min: 0, max: 200 },
  quantite:               { min: 1, max: 100_000 },
  experience_ans:         { min: 0, max: 70 },
  delai_preavis_h:        { min: 0, max: 720 },
  duree_intervention_h:   { min: 0, max: 240 },
  duree_estimee_min:      { min: 1, max: 14_400 }
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/**
 * Applique les améliorations frontend sur une définition de champ venant de l'API.
 * Retourne une nouvelle définition enrichie (immuable).
 *
 * @param {object} definition – définition brute issue de sanitizeListingDetailDefinitions
 * @returns {object}
 */
export const applyFieldEnhancement = (definition) => {
  const enhancement = FIELD_ENHANCEMENTS[definition.key];
  if (!enhancement) return definition;

  const enhanced = { ...definition };

  // Remplacement text → select avec options fixes
  if (enhancement.overrideType && enhancement.options) {
    enhanced.field_type    = enhancement.overrideType;
    enhanced.options       = enhancement.options.map((o) => o.value);
    enhanced._optionI18n   = enhancement.options; // map pour les traductions locales
  }

  // Contraintes numériques
  if (enhancement.min !== undefined) enhanced.min = enhancement.min;
  if (enhancement.max !== undefined) enhanced.max = enhancement.max;

  return enhanced;
};

/**
 * Traduit la valeur d'une option en consultant d'abord les traductions locales
 * de l'enhancement, puis en retournant la valeur brute en fallback.
 *
 * @param {string} value     – valeur stockée (= label français)
 * @param {object} definition – définition enrichie (peut contenir _optionI18n)
 * @param {string} language  – 'fr' | 'en' | 'ar'
 * @returns {string}
 */
export const translateEnhancedOption = (value, definition, language) => {
  const i18nMap = definition._optionI18n;
  if (!i18nMap) return null; // pas d'override → laisser le appelant gérer

  const entry = i18nMap.find((o) => o.value === value);
  if (!entry) return value;

  if (language === 'ar' && entry.ar) return entry.ar;
  if (language === 'en' && entry.en) return entry.en;
  return entry.value; // fallback fr
};
